/**
 * The scan surface: capture a card, let the cascade decide, file it.
 *
 * Capturing is either live (the camera stays open and fires when a card settles, ADR
 * 0014) or a single photo through the file input, which stays as the fallback for a
 * denied permission, a desktop, or a card that will not sit still.
 *
 * The three lists on this screen are the whole product, and the split between them
 * is deliberate:
 *
 *  - **Ready to file** - the cascade committed, or a human resolved a review. A
 *    commit sends them as one batch, because that is the shape the intake route was
 *    built for: one transaction, one slot per card, and the same call an offline
 *    queue will replay. Each card's scan id is minted here, which is what makes a
 *    retry safe rather than double-counting.
 *  - **Needs a person** - low confidence, an illustration printed many times, or a
 *    picture that matched nothing. Intake does not stop for these. Each one carries
 *    the cascade's candidates and its own explanation of why it did not decide.
 *  - **Filed** - the address the API reported for each copy. This is the promise:
 *    the card is somewhere, and the app can say where.
 *
 * A capture that holds no card is refused with the core's own words rather than
 * recorded as a failure of the scan: a phone pointed at a table is a photograph of
 * a table, and the answer is another photograph.
 */

import { useCallback, useRef, useState } from 'react';
import { type ArtIndex, type FrameClass, type RecognitionCandidate } from '@unsorted/shared';
import { ApiError, type Api } from '../api/http.js';
import type { IntakeSession, Meta, ScanResult } from '../api/types.js';
import { Banner } from '../components/Banner.js';
import { PrintingPicker } from '../components/PrintingPicker.js';
import { capacitorBridge } from '../ota/host.js';
import { browserCameraSource, type CameraSource } from '../scan/camera-source.js';
import { captureFile, type CaptureRead, type CaptureSource } from '../scan/capture.js';
import { LiveScanner, type LiveCaptureOutcome } from '../scan/LiveScanner.js';
import { liveScanPlugin, nativeCameraSource } from '../scan/native-camera.js';
import { describeLocalMatch, matchArtworkLocally, type LocalArtMatch } from '../scan/offline.js';
import {
  countBatch,
  newScanId,
  printingLabel,
  readyItems,
  reviewItems,
  scanRequestBody,
  statusForOutcome,
  toChosenPrinting,
  type ChosenPrinting,
  type ScanItem,
} from '../scan/queue.js';

export interface ScanSurfaceProps {
  api: Api;
  session: IntakeSession;
  meta: Meta | null;
  /** Injected in tests, where there is no canvas to decode a capture with. */
  capture?: CaptureSource;
  /**
   * The art index installed on this device, for the scans the catalog cannot answer.
   *
   * Absent in a browser tab, where there is no such artifact and the server is the
   * only index there is.
   */
  localArtIndex?: () => Promise<ArtIndex | null>;
  /** The session the commit answered with: its copy count is the batch's progress. */
  onSession: (session: IntakeSession) => void;
  onFiled: (printings: ChosenPrinting[]) => void;
  targetAddress: string;
}

/**
 * True when the request never reached the server.
 *
 * The distinction matters: a server that answered - a 401, a 500, a refusal - is a
 * server that can be retried or signed into, and naming an artwork "because the
 * catalog could not be reached" would be a lie about what happened.
 */
const isUnreachable = (failure: unknown): boolean => failure instanceof ApiError && failure.status === 0;

const readError = (failure: unknown): string =>
  failure instanceof ApiError
    ? failure.message
    : failure instanceof Error
      ? failure.message
      : 'something went wrong';

/**
 * Whether this device should open the live scanner rather than a file input.
 *
 * Live is the default, because scanning a pile is the job: a card that fills the guide
 * is taken by itself, whereas a file input asks for a press, a picker and a shutter
 * once per card. It used to be offered only on a coarse pointer, which meant a tablet
 * with a mouse - and any browser whose `matchMedia` said nothing useful - opened on the
 * slower of the two surfaces. The one case that is *not* live by default is a device
 * with no camera API at all, where the sheet would open only to say it has nothing to
 * open; that case keeps the file input, and the tabs let anyone switch either way.
 */
const prefersLive = (): boolean => {
  try {
    // The shell does not need `getUserMedia`: its camera is the native plugin, and a
    // webview old enough to lack the browser API is still a phone that can scan.
    if (liveScanPlugin(capacitorBridge()) !== null) return true;
    return navigator.mediaDevices?.getUserMedia !== undefined;
  } catch {
    return false;
  }
};

/** The sentence shown beside a committed scan: where the card went. */
function describeResult(result: ScanResult): string {
  if (result.kind === 'duplicate') {
    return result.location
      ? `Already on a shelf: ${result.location.address} (this scan was replayed, so no second copy was made)`
      : 'Already filed for this scan; no second copy was made.';
  }
  const where = result.location ? `Filed into ${result.location.address}` : 'Filed, with no bin recorded';
  const warnings = result.warnings.map((warning) =>
    warning === 'bin_over_capacity'
      ? ' (that bin is at its stated capacity)'
      : ' (no bin had room, so it went into the first one)',
  );
  return `${where}${warnings.join('')}`;
}

export function ScanSurface({
  api,
  session,
  meta,
  capture,
  localArtIndex,
  onSession,
  onFiled,
  targetAddress,
}: ScanSurfaceProps) {
  const [items, setItems] = useState<ScanItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [captureMode, setCaptureMode] = useState<'photo' | 'live'>(() => (prefersLive() ? 'live' : 'photo'));

  const readSource: CaptureSource = capture ?? captureFile;
  const counts = countBatch(items);
  const recognisable = meta === null || meta.recognition.available;

  // The installed index is read once per sitting rather than per capture: it is 1.6 MB
  // and it does not change under a batch. A failed read is remembered as "no index",
  // because the next capture needs an answer, not another attempt at the same file.
  const installedIndex = useRef<Promise<ArtIndex | null> | null>(null);

  /** The artwork this capture is, according to the index on the device. */
  const matchOnThisDevice = async (artPhash: string | null): Promise<LocalArtMatch | null> => {
    if (artPhash === null || localArtIndex === undefined) return null;
    installedIndex.current ??= localArtIndex().catch(() => null);
    const index = await installedIndex.current;
    return index === null ? null : matchArtworkLocally(index, artPhash);
  };

  const patch = (scanId: string, change: Partial<ScanItem>): void =>
    setItems((current) => current.map((item) => (item.scanId === scanId ? { ...item, ...change } : item)));

  /**
   * What the API says about a read, applied to its row. Returns the line a sheet shows.
   *
   * Split from the decode so the live scanner can hand over a read it already made -
   * a phone's still arrives as grey levels, not as a file, and re-encoding it to JPEG
   * to feed the decoder would be a second lossy trip for pixels the detector just saw.
   */
  const recognise = async (scanId: string, read: CaptureRead, artPhash: string): Promise<string> => {
    try {
      const { report } = await api.recognise({
        artPhash,
        frameClass: read.frameClass,
        mode: session.recognitionMode,
        scanId,
      });
      const chosen = report.chosen === null ? null : toChosenPrinting(report.chosen);
      const label =
        chosen === null
          ? 'Needs a person: the picture matched, but not one printing.'
          : `${report.outcome === 'auto_committed' ? 'Read' : 'Chosen'}: ${printingLabel(chosen)}`;
      patch(scanId, {
        status: statusForOutcome(report.outcome, chosen !== null),
        frameClass: read.frameClass,
        artPhash: read.artPhash,
        confidence: report.confidence,
        chosen,
        candidates: report.candidates,
        notes: [...read.notes, ...report.notes],
        report,
        message: chosen === null ? null : label,
      });
      return label;
    } catch (failure) {
      // The catalog could not be reached at all. The index on the device can still say
      // which artwork this is, and that turns a lost scan into a one-line job for a
      // person: the set code and number are printed on the card in their hand.
      const match = isUnreachable(failure) ? await matchOnThisDevice(read.artPhash) : null;
      if (match !== null) {
        patch(scanId, {
          status: 'review',
          frameClass: read.frameClass,
          artPhash: read.artPhash,
          confidence: 0,
          chosen: null,
          candidates: [],
          // No report, so no evidence: no cascade ran, and the numbers the recognition
          // modes are judged by are not the device's to invent.
          report: null,
          notes: [...read.notes, describeLocalMatch(match)],
          message:
            'Matched on this device: the catalog could not be reached, so confirm the printing by its set code and number.',
        });
        return 'Matched on this device — confirm the printing.';
      }
      patch(scanId, { status: 'error', message: readError(failure) });
      return `Not read: ${readError(failure)}`;
    }
  };

  /** A row for a read the device already made: the batch's one entry point. */
  const addRead = (read: CaptureRead, label: string): ScanItem => {
    const item: ScanItem = {
      scanId: newScanId(),
      addedAt: new Date().toISOString(),
      label,
      status: 'reading',
      frameClass: read.frameClass,
      artPhash: read.artPhash,
      confidence: 0,
      chosen: null,
      report: null,
      candidates: [],
      notes: read.notes,
      message: null,
      location: null,
    };
    setItems((current) => [...current, item]);
    return item;
  };

  /** Decode a capture, read the card, and ask the API what it is. */
  const readOne = async (scanId: string, file: Blob): Promise<void> => {
    let read: CaptureRead;
    try {
      read = await readSource(file);
    } catch (failure) {
      patch(scanId, { status: 'error', message: readError(failure) });
      return;
    }
    if (read.artPhash === null) {
      patch(scanId, {
        status: 'refused',
        frameClass: read.frameClass,
        notes: read.notes,
        message: 'No card was found in that capture. Put the card on a plain surface and fill the frame with it.',
      });
      return;
    }
    await recognise(scanId, read, read.artPhash);
  };

  /**
   * The live scanner's capture: a read, not a file.
   *
   * A frame with no card in it never becomes a row. The scanner fires on a tripwire,
   * and a tripwire that was wrong is a framing failure the sheet retries in place, not
   * a card the batch has to carry and a person has to clear.
   */
  const onLiveCapture = async (read: CaptureRead, index: number): Promise<LiveCaptureOutcome> => {
    if (read.artPhash === null) {
      return { status: 'refused', label: 'No card was found in that frame — fill the guide and hold still.' };
    }
    const item = addRead(read, `live ${index}`);
    return { status: 'accepted', label: await recognise(item.scanId, read, read.artPhash) };
  };

  /** The camera this device has: the shell's when it is there, the browser's otherwise. */
  const cameraSource = useCallback((video: HTMLVideoElement | null): CameraSource | null => {
    const plugin = liveScanPlugin(capacitorBridge());
    if (plugin !== null) return nativeCameraSource(plugin);
    if (video === null) return null;
    return browserCameraSource({ video });
  }, []);

  const addBlobs = (chosen: Blob[], names: (index: number) => string): void => {
    if (chosen.length === 0) return;
    const added: ScanItem[] = chosen.map((_file, index) => ({
      scanId: newScanId(),
      addedAt: new Date().toISOString(),
      label: names(index) === '' ? `capture ${items.length + index + 1}` : names(index),
      status: 'reading',
      frameClass: 'unknown' as FrameClass,
      artPhash: null,
      confidence: 0,
      chosen: null,
      report: null,
      candidates: [],
      notes: [],
      message: null,
      location: null,
    }));
    setItems((current) => [...current, ...added]);
    added.forEach((item, index) => {
      const file = chosen[index];
      if (file) void readOne(item.scanId, file);
    });
  };

  const onFiles = (files: FileList | null): void => {
    if (files === null || files.length === 0) return;
    const chosen = [...files];
    addBlobs(chosen, (index) => chosen[index]?.name ?? '');
  };

  const commit = async (): Promise<void> => {
    const ready = readyItems(items);
    if (ready.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      // The session comes back with the batch, and its `copiesAdded` is what the
      // header shows: without reporting it, a user who just filed five cards still
      // reads "0 cards filed" while the copies exist.
      const { intakeSession, results } = await api.commitScans(session.publicId, ready.map(scanRequestBody));
      onSession(intakeSession);
      const byScan = new Map(results.map((result) => [result.scanId, result]));
      setItems((current) =>
        current.map((item) => {
          const result = byScan.get(item.scanId);
          if (!result) return item;
          return {
            ...item,
            status: result.kind === 'placed' ? 'filed' : 'duplicate',
            location: result.location,
            message: describeResult(result),
          };
        }),
      );
      const filed = ready.filter((item) => byScan.get(item.scanId)?.kind === 'placed');
      onFiled(filed.map((item) => item.chosen).filter((value): value is ChosenPrinting => value !== null));
    } catch (failure) {
      setError(`The batch was not filed: ${readError(failure)}`);
    } finally {
      setBusy(false);
    }
  };

  /** A human resolved a review: the pick joins the batch that will be filed. */
  const resolve = (scanId: string, printing: ChosenPrinting): void =>
    patch(scanId, { status: 'ready', chosen: printing, message: `You chose: ${printingLabel(printing)}` });

  /** A card filed by hand, with no capture at all. */
  const fileByHand = (printing: ChosenPrinting): void => {
    setItems((current) => [
      ...current,
      {
        scanId: newScanId(),
        addedAt: new Date().toISOString(),
        label: 'typed in',
        status: 'ready',
        frameClass: 'unknown' as FrameClass,
        artPhash: null,
        confidence: 1,
        chosen: printing,
        // No report and no evidence: a person entered this card, and the trail
        // should say so rather than claim a reading nothing measured.
        report: null,
        candidates: [],
        notes: [],
        message: `Typed in: ${printingLabel(printing)}`,
        location: null,
      },
    ]);
    setManualOpen(false);
  };

  const filed = items.filter((item) => item.status === 'filed' || item.status === 'duplicate');
  const refused = items.filter((item) => item.status === 'refused' || item.status === 'error');

  return (
    <div className="stack">
      {error && <Banner kind="error">{error}</Banner>}

      <div className="card">
        <div className="row capture-modes" role="group" aria-label="Capture mode">
          <button
            type="button"
            className={captureMode === 'live' ? 'tab current' : 'tab'}
            aria-pressed={captureMode === 'live'}
            onClick={() => setCaptureMode('live')}
          >
            Live scanner
          </button>
          <button
            type="button"
            className={captureMode === 'photo' ? 'tab current' : 'tab'}
            aria-pressed={captureMode === 'photo'}
            onClick={() => setCaptureMode('photo')}
          >
            Single photo
          </button>
        </div>
        {captureMode === 'photo' ? (
          <label className="capture">
            <span className="capture-title">{recognisable ? 'Scan a card' : 'Recognition is off on this server'}</span>
            <span className="hint">
              {recognisable
                ? 'Take a photo, or pick one you already have. The picture is read here; the catalog lives on the server.'
                : 'Cards can still be filed by their set code and number.'}
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              disabled={!recognisable}
              aria-label="Capture a card"
              onChange={(event) => {
                onFiles(event.target.files);
                // Allow the same file twice: a scan that failed should be retryable.
                event.target.value = '';
              }}
            />
          </label>
        ) : (
          <div className="capture-batch">
            <span className="capture-title">{recognisable ? 'Live scan' : 'Recognition is off on this server'}</span>
            <span className="hint">
              {recognisable
                ? 'The camera stays open. A card that fills the guide is taken by itself; every capture joins the batch below, so scanning never stops to ask.'
                : 'Cards can still be filed by their set code and number.'}
            </span>
            {recognisable && (
              <LiveScanner source={cameraSource} onCapture={onLiveCapture} startAt={items.length + 1} />
            )}
          </div>
        )}
        <p className="hint" role="status" aria-live="polite">
          {counts.reading > 0 ? (
            <span className="tnum">Reading {counts.reading} capture(s)…</span>
          ) : (
            <span>Ready: {targetAddress}</span>
          )}
        </p>
      </div>

      {items.length === 0 && (
        <div className="card">
          <h3>Nothing scanned yet</h3>
          <p className="hint">
            Cards are filed at the next free slot in {targetAddress}. Read cards are filed straight away; anything the
            cascade is unsure about waits below for you, so scanning never stops to ask.
          </p>
          <button type="button" className="ghost" onClick={() => setManualOpen(true)}>
            File a card by hand instead
          </button>
        </div>
      )}

      {counts.ready > 0 && (
        <div className="card commit" aria-live="polite">
          <h3>
            Ready to file: {counts.ready} card{counts.ready === 1 ? '' : 's'}
          </h3>
          <ul className="items">
            {readyItems(items).map((item) => (
              <li key={item.scanId}>
                <span>{item.chosen ? printingLabel(item.chosen) : item.label}</span>
                <span className="hint">{item.message}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="primary" disabled={busy} onClick={() => void commit()}>
            {busy ? 'Filing…' : `File ${counts.ready} card${counts.ready === 1 ? '' : 's'} into ${targetAddress}`}
          </button>
        </div>
      )}

      {reviewItems(items).length > 0 && (
        <div className="card review" aria-live="polite">
          <h3>Needs a person: {reviewItems(items).length}</h3>
          <p className="hint">
            Intake did not stop for these. Confirm one and it joins the batch; the picture matched but could not say
            which printing it was.
          </p>
          <ul className="items">
            {reviewItems(items).map((item) => (
              <li key={item.scanId} className="review-item">
                <header>
                  <strong>{item.label}</strong>
                  <span className="hint">
                    {item.status === 'failed'
                      ? 'the picture matched nothing in the index'
                      : item.report === null
                        ? 'matched on this device — the catalog could not be reached'
                        : `best ${Math.round(item.confidence * 100)}%`}
                    {item.artPhash ? ` · ${item.frameClass}` : ''}
                  </span>
                </header>
                {item.notes.length > 0 && (
                  <details>
                    <summary>Why it stopped</summary>
                    <ul className="notes">
                      {item.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </details>
                )}
                <PrintingPicker
                  api={api}
                  candidates={item.candidates as RecognitionCandidate[]}
                  onChoose={(printing) => resolve(item.scanId, printing)}
                  prompt="The card says"
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {refused.length > 0 && (
        <div className="card">
          <h3>Nothing filed from {refused.length} capture{refused.length === 1 ? '' : 's'}</h3>
          <ul className="items">
            {refused.map((item) => (
              <li key={item.scanId}>
                <span>{item.label}</span>
                <span className="hint">{item.message}</span>
              </li>
            ))}
          </ul>
          <button type="button" className="ghost" onClick={() => setItems((current) => current.filter((item) => item.status !== 'refused' && item.status !== 'error'))}>
            Clear them
          </button>
        </div>
      )}

      {filed.length > 0 && (
        <div className="card filed" aria-live="polite">
          <h3>Filed: {filed.length}</h3>
          <ul className="items">
            {filed.map((item) => (
              <li key={item.scanId}>
                <span>{item.chosen ? printingLabel(item.chosen) : item.label}</span>
                <span className="hint">{item.message}</span>
              </li>
            ))}
          </ul>
          <p className="hint">The address above is the copy's own location record: the Pulls tab reads it back the same way.</p>
        </div>
      )}

      {manualOpen && (
        <div className="card">
          <h3>File a card by hand</h3>
          <p className="hint">No capture, no evidence: the copy records that a person entered it.</p>
          <PrintingPicker api={api} onChoose={fileByHand} prompt="The card says" />
          <button type="button" className="ghost" onClick={() => setManualOpen(false)}>
            Cancel
          </button>
        </div>
      )}

      {items.length > 0 && counts.ready === 0 && counts.review === 0 && counts.failed === 0 && !manualOpen && (
        <button type="button" className="ghost" onClick={() => setManualOpen(true)}>
          File another card by hand
        </button>
      )}
    </div>
  );
}
